import { MapPinIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { DestinationType, Hotel, LocalStorageRecentSearches } from "components/HeroSearchFormSmall/LocationInput";
import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";
import React, { useState, useEffect, useRef, FC } from "react";
import { ApisService } from "services/api";
import { debounce } from "lodash";
import { HiOutlineOfficeBuilding } from "react-icons/hi";

interface Props {
  onClick?: () => void;
  onChange?: (value: string) => void;
  className?: string;
  defaultValue?: string;
  headingText?: string;
}

const LocationInput: FC<Props> = ({
  onChange = () => { },
  className = "",
  headingText = "Para onde?",
}) => {
  const [value, setValue] = useState("");
  const [destinations, setDestinations] = useState<DestinationType | null>();
  const {
    setDestination,
    setDestinationName,
    destinationName,
    setGetHotelSearchFirst
  } = useHotels();

  const containerRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const storedDestinationsString = localStorage.getItem(
    "recentSearchesDestinations"
  );

  useEffect(() => {
    if (storedDestinationsString) {
      const localRecentSearchesDestinations: LocalStorageRecentSearches[] =
        JSON.parse(storedDestinationsString);
      const newRecentSearches = localRecentSearchesDestinations.filter(item => item.type);
      if (newRecentSearches.length === 0) {
        localStorage.removeItem("recentSearchesDestinations");
      }
    }
  }, []);

  useEffect(() => {
    if (destinationName) {
      if (inputRef.current) {
        inputRef.current.value = destinationName;
      }
      setValue(destinationName);
    }
  }, [destinationName]);

  const debounceChange = debounce(e => {
    if (e.target.value.length >= 3) {
      setValue(e.target.value)
      ApisService.getDestinations(e.target.value).then((response) => {
        setDestinations(response);
      })
    } else {
      setDestinations(null);
    }
  }, 500);

  const handleChangeInputValue = (e: React.ChangeEvent<HTMLInputElement>) => debounceChange(e)

  const handleSelectDestination = (destination: LocalStorageRecentSearches) => {
    if (destination.type === "HOTEL") {
      setDestination((destination as Hotel).parent_id);
      setGetHotelSearchFirst(destination.id);
    } else {
      setDestination(destination.id);
    }
    setDestinationName(destination.name);

    const localRecentSearchesDestinations: LocalStorageRecentSearches[] =
      storedDestinationsString
        ? JSON.parse(storedDestinationsString)
        : [];

    if (localRecentSearchesDestinations.filter(item => item.id === destination.id).length === 0) {
      const newRecentSearches = [destination, ...localRecentSearchesDestinations];
      localStorage.setItem(
        "recentSearchesDestinations",
        JSON.stringify(newRecentSearches)
      );
    }
  }

  const renderRecentSearches = () => {
    const localRecentSearchesDestinations: LocalStorageRecentSearches[] =
      storedDestinationsString
        ? JSON.parse(storedDestinationsString)
        : undefined;
    return (
      <>
        <p className="block font-semibold text-base">Buscas recentes</p>
        <div className="mt-3">
          {localRecentSearchesDestinations ? (
            localRecentSearchesDestinations.map((item: LocalStorageRecentSearches) => {
              return (
                <div
                  className="flex px-4 sm:px-6 items-center space-x-3 text-sm py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer overflow-y-auto"
                  onClick={() => handleSelectDestination(item)}
                  key={item.id}
                >
                  {item.type === "CITY"
                    ? <MapPinIcon className="h-4 w-4 sm:h-6 sm:w-6" />
                    : <HiOutlineOfficeBuilding className="h-4 w-4 sm:h-6 sm:w-6" />
                  }
                  <span className="block text-neutral-700 dark:text-neutral-200">{item.name}</span>
                </div>
              );
            })
          ) : (
            <p className="mt-2 text-sm text-neutral-800 dark:text-neutral-100">
              Não há buscas recentes
            </p>
          )}
        </div>
      </>
    );
  };

  const renderSearchValues = () => {
    return (
      <>
        <div className="flex items-center gap-1">
          <span className="text-neutral-500">
            <MapPinIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </span>
          <p>
            Cidades
          </p>
        </div>
        <div className="mt-3">
          {destinations && destinations.cities.length > 0 &&
            destinations.cities.slice(0, 4).map((item) => (
              <div
                className="py-2 mb-1 flex items-center space-x-3 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                onClick={() => handleSelectDestination(item as unknown as LocalStorageRecentSearches)}
                key={item.id}
              >
                <span className="">{item.name}</span>
              </div>
            ))}
          {
            destinations && destinations.cities.length === 0 && destinations.hotels.length > 0 &&
            <span
              className="flex px-4 sm:px-6 items-center space-x-3 py-4"
            >
              <span className="block text-neutral-700 dark:text-neutral-200">
                Nenhum resultado para {value}
              </span>
            </span>
          }
        </div>
      </>
    );
  };

  return (
    <div className={`${className}`} ref={containerRef}>
      <div className="p-5">
        <span className="block font-semibold text-xl sm:text-2xl">
          {headingText}
        </span>
        <div className="relative mt-5">
          <input
            className={`block w-full bg-transparent border px-4 py-3 pr-12 border-neutral-900 dark:border-neutral-200 rounded-xl focus:ring-0 focus:outline-none text-base leading-none placeholder-neutral-500 dark:placeholder-neutral-300 truncate font-bold placeholder:truncate`}
            placeholder={"Buscar destinos"}
            onChange={handleChangeInputValue}
            ref={inputRef}
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <MagnifyingGlassIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-400" />
          </span>
        </div>
        <div className="mt-7">
          {(value && destinations)
            ? renderSearchValues()
            : renderRecentSearches()}
          {destinations && destinations.hotels && destinations.hotels.length > 0 && (
            <div className="flex items-center sm:px-6 my-4 gap-1">
              <span className="text-neutral-500">
                <HiOutlineOfficeBuilding className="h-5 w-5 sm:h-6 sm:w-6" />
              </span>
              <p>
                Hotéis
              </p>
            </div>
          )}
          {destinations?.hotels?.slice(0, 4).map((item) => (
            <span
              onClick={() => handleSelectDestination(item as unknown as LocalStorageRecentSearches)}
              key={item.id}
              className="py-2 mb-1 flex items-center space-x-3 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
            >
              <span>
                {item.name}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LocationInput;
